import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Col, Row, Form } from "react-bootstrap";
import { toast } from "react-toastify";

const EditableField = ({
  label,
  name,
  value,
  error,
  onChange,
  onSave,
  originalValue,
  type = "text",
  options,
  isSaving,
  isEditMode,
  disabled,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [internalValue, setInternalValue] = useState(value || "");

  useEffect(() => {
    setInternalValue(value || "");
  }, [value]);

  // In create mode, always update directly
  const handleChange = (e) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    onChange({ target: { name, value: newValue } });
  };

  // Only use Edit/Save pattern in edit mode
  const toggleEdit = async () => {
    if (isEditing) {
      // Save mode - only in edit mode
      const hasChanged = internalValue !== (originalValue || "");
      if (!hasChanged) {
        toast.info("No changes detected");
        setIsEditing(false);
        return;
      }

      try {
        await onSave(name, internalValue);
        toast.success(`${label} updated successfully!`);
        setIsEditing(false);
      } catch (error) {
        toast.error(`Failed to update ${label}: ${error.message}`);
        // Keep editing mode on error
      }
    } else {
      setIsEditing(true);
    }
  };

  // In create mode, show regular input (no Edit/Save)
  if (!isEditMode || !onSave) {
    return (
      <Form.Group controlId={name} className="mb-3">
        <Form.Label>{label}</Form.Label>
        {type === "select" ? (
          <Form.Select
            name={name}
            value={value || ""}
            onChange={handleChange}
            isInvalid={!!error}
          >
            {options &&
              options.map((opt) => (
                <option key={opt} value={opt}>
                  {opt || "Select..."}
                </option>
              ))}
          </Form.Select>
        ) : (
          <Form.Control
            type={type}
            name={name}
            value={value || ""}
            onChange={handleChange}
            disabled={disabled}
            isInvalid={!!error}
          />
        )}
        <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
      </Form.Group>
    );
  }

  // In edit mode, show Edit/Save pattern

  return (
    <Form.Group controlId={name} className="mb-3">
      <div
        className="d-flex justify-content-between align-items-center mb-1"
        style={{ userSelect: "none" }}
      >
        <Form.Label className="mb-0">{label}</Form.Label>
        <span
          role="button"
          tabIndex={0}
          onClick={toggleEdit}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") toggleEdit();
          }}
          style={{
            cursor: isSaving ? "wait" : "pointer",
            color: "#0d6efd",
            fontSize: "0.9rem",
            fontWeight: "500",
            opacity: isSaving ? 0.6 : 1,
          }}
        >
          {isSaving ? "Saving..." : isEditing ? "Save" : "Edit"}
        </span>
      </div>

      {type === "select" ? (
        <Form.Select
          name={name}
          value={internalValue}
          onChange={(e) => setInternalValue(e.target.value)}
          disabled={!isEditing}
          isInvalid={!!error}
        >
          {options &&
            options.map((opt) => (
              <option key={opt} value={opt}>
                {opt || "Select..."}
              </option>
            ))}
        </Form.Select>
      ) : (
        <Form.Control
          type={type}
          name={name}
          value={internalValue}
          onChange={(e) => setInternalValue(e.target.value)}
          disabled={!isEditing}
          isInvalid={!!error}
        />
      )}

      <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
    </Form.Group>
  );
};

EditableField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  error: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  originalValue: PropTypes.string,
  type: PropTypes.string,
  options: PropTypes.array,
  isSaving: PropTypes.bool,
  isEditMode: PropTypes.bool,
};

const AddressUserForm = ({
  formData,
  errors = {},
  handleInputChange,
  userId,
  patchProfileProperties,
  refetch,
  isEditMode,
}) => {
  const [originalValues, setOriginalValues] = useState({});
  const [savingFields, setSavingFields] = useState({});

  // Initialize original values
  useEffect(() => {
    const addressFields = [
      "permanentStreet",
      "permanentCity",
      "permanentState",
      "permanentZipCode",
      "permanentCountry",
      "residentialStreet",
      "residentialCity",
      "residentialState",
      "residentialZipCode",
      "residentialCountry",
      "workStreet",
      "workCity",
      "workState",
      "workZipCode",
      "workCountry",
      "schoolStreet",
      "schoolCity",
      "schoolState",
      "schoolZipCode",
      "schoolCountry",
      "billingStreet",
      "billingCity",
      "billingState",
      "billingZipCode",
      "billingCountry",
      "preferredMailingAddress",
      "preferredContactMethod",
    ];
    const originals = {};
    addressFields.forEach((field) => {
      originals[field] = formData[field] || "";
    });
    setOriginalValues(originals);
  }, [formData]);

  // Map form field names to API structure
  const getAddressFieldMapping = (fieldName) => {
    const addressFields = {
      permanentStreet: { addressType: "permanentAddress", field: "street" },
      permanentCity: { addressType: "permanentAddress", field: "city" },
      permanentState: { addressType: "permanentAddress", field: "state" },
      permanentZipCode: { addressType: "permanentAddress", field: "zipCode" },
      permanentCountry: { addressType: "permanentAddress", field: "country" },
      residentialStreet: { addressType: "residentialAddress", field: "street" },
      residentialCity: { addressType: "residentialAddress", field: "city" },
      residentialState: { addressType: "residentialAddress", field: "state" },
      residentialZipCode: {
        addressType: "residentialAddress",
        field: "zipCode",
      },
      residentialCountry: {
        addressType: "residentialAddress",
        field: "country",
      },
      workStreet: { addressType: "workAddress", field: "street" },
      workCity: { addressType: "workAddress", field: "city" },
      workState: { addressType: "workAddress", field: "state" },
      workZipCode: { addressType: "workAddress", field: "zipCode" },
      workCountry: { addressType: "workAddress", field: "country" },
      schoolStreet: { addressType: "schoolAddress", field: "street" },
      schoolCity: { addressType: "schoolAddress", field: "city" },
      schoolState: { addressType: "schoolAddress", field: "state" },
      schoolZipCode: { addressType: "schoolAddress", field: "zipCode" },
      schoolCountry: { addressType: "schoolAddress", field: "country" },
      billingStreet: { addressType: "billingAddress", field: "street" },
      billingCity: { addressType: "billingAddress", field: "city" },
      billingState: { addressType: "billingAddress", field: "state" },
      billingZipCode: { addressType: "billingAddress", field: "zipCode" },
      billingCountry: { addressType: "billingAddress", field: "country" },
    };

    return addressFields[fieldName] || null;
  };

  const handleFieldSave = async (fieldName, value) => {
    if (!userId || !patchProfileProperties) {
      handleInputChange({ target: { name: fieldName, value } });
      return;
    }

    setSavingFields((prev) => ({ ...prev, [fieldName]: true }));

    try {
      const addressMapping = getAddressFieldMapping(fieldName);

      if (addressMapping) {
        // Address field - structure as nested object
        await patchProfileProperties(userId, {
          [addressMapping.addressType]: {
            [addressMapping.field]: value,
          },
        });
      } else if (
        fieldName === "preferredMailingAddress" ||
        fieldName === "preferredContactMethod"
      ) {
        // Direct profile fields
        await patchProfileProperties(userId, {
          [fieldName]: value,
        });
      } else {
        throw new Error(`Unknown field: ${fieldName}`);
      }

      setOriginalValues((prev) => ({
        ...prev,
        [fieldName]: value,
      }));

      handleInputChange({ target: { name: fieldName, value } });

      if (refetch) {
        await refetch();
      }
    } finally {
      setSavingFields((prev) => {
        const newState = { ...prev };
        delete newState[fieldName];
        return newState;
      });
    }
  };
  return (
    <Row className="g-4">
      <Col xs={12}>
        <h6
          className="text-700 fw-semibold mb-2 mt-3"
          style={{ fontSize: "0.95rem", letterSpacing: "0.5px" }}
        >
          Permanent Address
        </h6>
      </Col>
      <Col md={6}>
        <EditableField
          label="Street"
          name="permanentStreet"
          value={formData.permanentStreet || ""}
          error={errors.permanentStreet}
          onChange={handleInputChange}
          onSave={null}
          originalValue={originalValues.permanentStreet}
          isSaving={false}
          isEditMode={false}
          disabled={isEditMode}

        />
      </Col>
      <Col md={6}>
        <EditableField
          label="City"
          name="permanentCity"
          value={formData.permanentCity || ""}
          error={errors.permanentCity}
          onChange={handleInputChange}
          onSave={null}
          originalValue={originalValues.permanentCity}
          isSaving={false}
          isEditMode={false}
          disabled={isEditMode}
        />
      </Col>
      <Col md={6}>
        <EditableField
          label="State"
          name="permanentState"
          value={formData.permanentState || ""}
          error={errors.permanentState}
          onChange={handleInputChange}
          onSave={null}
          originalValue={originalValues.permanentState}
          isSaving={false}
          isEditMode={false}
          disabled={isEditMode}
        />
      </Col>
      <Col md={6}>
        <EditableField
          label="Zip Code"
          name="permanentZipCode"
          value={formData.permanentZipCode || ""}
          error={errors.permanentZipCode}
          onChange={handleInputChange}
          onSave={handleFieldSave}
          originalValue={originalValues.permanentZipCode}
          isSaving={false}
          isEditMode={false}
          disabled={isEditMode}
        />
      </Col>
      <Col md={6}>
        <EditableField
          label="Country"
          name="permanentCountry"
          value={formData.permanentCountry || ""}
          error={errors.permanentCountry}
          onChange={handleInputChange}
          onSave={null}
          originalValue={originalValues.permanentCountry}
          isSaving={false}
          isEditMode={false}
          disabled={isEditMode}
        />
      </Col>

      {/* <Col xs={12}>
        <h6 className="text-700 fw-semibold mb-2 mt-3" style={{ fontSize: '0.95rem', letterSpacing: '0.5px' }}>
          Residential Address
        </h6>
      </Col>
      <Col md={6}>
        <EditableField
          label="Street"
          name="residentialStreet"
          value={formData.residentialStreet || ''}
          error={errors.residentialStreet}
          onChange={handleInputChange}
          onSave={handleFieldSave}
          originalValue={originalValues.residentialStreet}
          isSaving={savingFields.residentialStreet}
        />
      </Col>
      <Col md={6}>
        <EditableField
          label="City"
          name="residentialCity"
          value={formData.residentialCity || ''}
          error={errors.residentialCity}
          onChange={handleInputChange}
          onSave={handleFieldSave}
          originalValue={originalValues.residentialCity}
          isSaving={savingFields.residentialCity}
        />
      </Col>
      <Col md={6}>
        <EditableField
          label="State"
          name="residentialState"
          value={formData.residentialState || ''}
          error={errors.residentialState}
          onChange={handleInputChange}
          onSave={handleFieldSave}
          originalValue={originalValues.residentialState}
          isSaving={savingFields.residentialState}
        />
      </Col>
      <Col md={6}>
        <EditableField
          label="Zip Code"
          name="residentialZipCode"
          value={formData.residentialZipCode || ''}
          error={errors.residentialZipCode}
          onChange={handleInputChange}
          onSave={handleFieldSave}
          originalValue={originalValues.residentialZipCode}
          isSaving={savingFields.residentialZipCode}
        />
      </Col>
      <Col md={6}>
        <EditableField
          label="Country"
          name="residentialCountry"
          value={formData.residentialCountry || ''}
          error={errors.residentialCountry}
          onChange={handleInputChange}
          onSave={handleFieldSave}
          originalValue={originalValues.residentialCountry}
          isSaving={savingFields.residentialCountry}
        />
      </Col> */}

      {/* <Col xs={12}>
        <h6 className="text-700 fw-semibold mb-2 mt-3" style={{ fontSize: '0.95rem', letterSpacing: '0.5px' }}>
          Work Address
        </h6>
      </Col>
      <Col md={6}>
        <EditableField
          label="Street"
          name="workStreet"
          value={formData.workStreet || ''}
          error={errors.workStreet}
          onChange={handleInputChange}
          onSave={handleFieldSave}
          originalValue={originalValues.workStreet}
          isSaving={savingFields.workStreet}
        />
      </Col>
      <Col md={6}>
        <EditableField
          label="City"
          name="workCity"
          value={formData.workCity || ''}
          error={errors.workCity}
          onChange={handleInputChange}
          onSave={handleFieldSave}
          originalValue={originalValues.workCity}
          isSaving={savingFields.workCity}
        />
      </Col>
      <Col md={6}>
        <EditableField
          label="State"
          name="workState"
          value={formData.workState || ''}
          error={errors.workState}
          onChange={handleInputChange}
          onSave={handleFieldSave}
          originalValue={originalValues.workState}
          isSaving={savingFields.workState}
        />
      </Col>
      <Col md={6}>
        <EditableField
          label="Zip Code"
          name="workZipCode"
          value={formData.workZipCode || ''}
          error={errors.workZipCode}
          onChange={handleInputChange}
          onSave={handleFieldSave}
          originalValue={originalValues.workZipCode}
          isSaving={savingFields.workZipCode}
        />
      </Col>
      <Col md={6}>
        <EditableField
          label="Country"
          name="workCountry"
          value={formData.workCountry || ''}
          error={errors.workCountry}
          onChange={handleInputChange}
          onSave={handleFieldSave}
          originalValue={originalValues.workCountry}
          isSaving={savingFields.workCountry}
        />
      </Col>

      <Col xs={12}>
        <h6 className="text-700 fw-semibold mb-2 mt-3" style={{ fontSize: '0.95rem', letterSpacing: '0.5px' }}>
          School Address
        </h6>
      </Col>
      <Col md={6}>
        <EditableField
          label="Street"
          name="schoolStreet"
          value={formData.schoolStreet || ''}
          error={errors.schoolStreet}
          onChange={handleInputChange}
          onSave={handleFieldSave}
          originalValue={originalValues.schoolStreet}
          isSaving={savingFields.schoolStreet}
        />
      </Col>
      <Col md={6}>
        <EditableField
          label="City"
          name="schoolCity"
          value={formData.schoolCity || ''}
          error={errors.schoolCity}
          onChange={handleInputChange}
          onSave={handleFieldSave}
          originalValue={originalValues.schoolCity}
          isSaving={savingFields.schoolCity}
        />
      </Col>
      <Col md={6}>
        <EditableField
          label="State"
          name="schoolState"
          value={formData.schoolState || ''}
          error={errors.schoolState}
          onChange={handleInputChange}
          onSave={handleFieldSave}
          originalValue={originalValues.schoolState}
          isSaving={savingFields.schoolState}
        />
      </Col>
      <Col md={6}>
        <EditableField
          label="Zip Code"
          name="schoolZipCode"
          value={formData.schoolZipCode || ''}
          error={errors.schoolZipCode}
          onChange={handleInputChange}
          onSave={handleFieldSave}
          originalValue={originalValues.schoolZipCode}
          isSaving={savingFields.schoolZipCode}
        />
      </Col>
      <Col md={6}>
        <EditableField
          label="Country"
          name="schoolCountry"
          value={formData.schoolCountry || ''}
          error={errors.schoolCountry}
          onChange={handleInputChange}
          onSave={handleFieldSave}
          originalValue={originalValues.schoolCountry}
          isSaving={savingFields.schoolCountry}
        />
      </Col>

      <Col xs={12}>
        <h6 className="text-700 fw-semibold mb-2 mt-3" style={{ fontSize: '0.95rem', letterSpacing: '0.5px' }}>
          Billing Address
        </h6>
      </Col>
      <Col md={6}>
        <EditableField
          label="Street"
          name="billingStreet"
          value={formData.billingStreet || ''}
          error={errors.billingStreet}
          onChange={handleInputChange}
          onSave={handleFieldSave}
          originalValue={originalValues.billingStreet}
          isSaving={savingFields.billingStreet}
        />
      </Col>
      <Col md={6}>
        <EditableField
          label="City"
          name="billingCity"
          value={formData.billingCity || ''}
          error={errors.billingCity}
          onChange={handleInputChange}
          onSave={handleFieldSave}
          originalValue={originalValues.billingCity}
          isSaving={savingFields.billingCity}
        />
      </Col>
      <Col md={6}>
        <EditableField
          label="State"
          name="billingState"
          value={formData.billingState || ''}
          error={errors.billingState}
          onChange={handleInputChange}
          onSave={handleFieldSave}
          originalValue={originalValues.billingState}
          isSaving={savingFields.billingState}
        />
      </Col>
      <Col md={6}>
        <EditableField
          label="Zip Code"
          name="billingZipCode"
          value={formData.billingZipCode || ''}
          error={errors.billingZipCode}
          onChange={handleInputChange}
          onSave={handleFieldSave}
          originalValue={originalValues.billingZipCode}
          isSaving={savingFields.billingZipCode}
        />
      </Col>
      <Col md={6}>
        <EditableField
          label="Country"
          name="billingCountry"
          value={formData.billingCountry || ''}
          error={errors.billingCountry}
          onChange={handleInputChange}
          onSave={handleFieldSave}
          originalValue={originalValues.billingCountry}
          isSaving={savingFields.billingCountry}
        />
      </Col>

      <Col md={6}>
        <EditableField
          label="Preferred Mailing Address"
          name="preferredMailingAddress"
          value={formData.preferredMailingAddress || ''}
          error={errors.preferredMailingAddress}
          onChange={handleInputChange}
          onSave={handleFieldSave}
          originalValue={originalValues.preferredMailingAddress}
          isSaving={savingFields.preferredMailingAddress}
        />
      </Col>
      <Col md={6}>
        <EditableField
          label="Preferred Contact Method"
          name="preferredContactMethod"
          value={formData.preferredContactMethod || ''}
          error={errors.preferredContactMethod}
          onChange={handleInputChange}
          onSave={handleFieldSave}
          originalValue={originalValues.preferredContactMethod}
          isSaving={savingFields.preferredContactMethod}
        />
      </Col> */}
    </Row>
  );
};

AddressUserForm.propTypes = {
  formData: PropTypes.object.isRequired,
  errors: PropTypes.object,
  handleInputChange: PropTypes.func.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  patchProfileProperties: PropTypes.func,
  refetch: PropTypes.func,
  isEditMode: PropTypes.bool,
};

export default AddressUserForm;
